import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaBell, FaSearch, FaEnvelope, FaHome, FaFilter, FaChartLine, FaFileAlt, FaSignInAlt, FaUserCheck, FaClipboardCheck, FaPencilAlt, FaEye, FaMapMarkerAlt, FaCog, FaHistory, FaExternalLinkAlt, FaCopy, FaCheck, FaChevronDown } from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";
import AgentBadge from "../components/AgentBadge";
import { useViewScope } from "../contexts/ViewScopeContext";
import DEMO_PAST_NOTIFICATIONS from "../demo/demoPastNotifications";
import DemoBanner from "../demo/DemoBanner";
import useDemoNetworkGuard from "../demo/useDemoNetworkGuard";

// Normalise duplicates to a canonical key
function normaliseEventType(et) {
  if (et === "property_analysis_complete" || et === "agent_property_analysis_alert") return "property_analysis";
  if (et === "agent_start_enquiry_form_alert") return "start_enquiry_form";
  return et;
}

const EVENT_LABELS = {
  property_analysis: "Viewed Property Analysis",
  property_report_sent: "Property Report Sent",
  property_report_processing: "Property Report Processing",
  property_report_request: "Requested Property Report",
  start_enquiry_form: "Started Enquiry Form",
  start_request_appraisal_review: "Started Appraisal Review Request",
  submit_request_appraisal_review: "Submitted Appraisal Review Request",
  request_sign_in_code: "Requested Sign In",
  onboarding_complete: "Completed Onboarding",
  suburb_analysis: "Viewed Suburb Analysis",
  suburb_profile_view: "Visited Suburb Profile",
  view_appraisal: "Viewed Appraisal",
  save_notification_settings: "Saved Notification Settings",
};

const EVENT_COLORS = {
  property_analysis: { bg: "#f3e8ff", color: "#7c3aed", border: "#c4b5fd" },
  property_report_sent: { bg: "#ede9fe", color: "#6d28d9", border: "#a78bfa" },
  property_report_processing: { bg: "#fef9c3", color: "#a16207", border: "#fde047" },
  property_report_request: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  start_enquiry_form: { bg: "#fce7f3", color: "#be185d", border: "#f9a8d4" },
  start_request_appraisal_review: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  submit_request_appraisal_review: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  request_sign_in_code: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
  onboarding_complete: { bg: "#d1fae5", color: "#047857", border: "#6ee7b7" },
  suburb_analysis: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
  suburb_profile_view: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
  view_appraisal: { bg: "#fff1f2", color: "#be123c", border: "#fda4af" },
  save_notification_settings: { bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" },
};

const EVENT_ICONS = {
  property_analysis: FaChartLine,
  property_report_sent: FaFileAlt,
  property_report_processing: FaFileAlt,
  property_report_request: FaFileAlt,
  start_enquiry_form: FaPencilAlt,
  start_request_appraisal_review: FaClipboardCheck,
  submit_request_appraisal_review: FaClipboardCheck,
  request_sign_in_code: FaSignInAlt,
  onboarding_complete: FaUserCheck,
  suburb_analysis: FaMapMarkerAlt,
  suburb_profile_view: FaMapMarkerAlt,
  view_appraisal: FaEye,
  save_notification_settings: FaCog,
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function formatFullDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy"}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 2,
        display: "inline-flex",
        alignItems: "center",
        color: copied ? "#22c55e" : "#9ca3af",
        fontSize: 12,
        flexShrink: 0,
        transition: "color 0.2s",
      }}
    >
      {copied ? <FaCheck /> : <FaCopy />}
    </button>
  );
}

export default function PastNotifications() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { viewScope, agentFilter } = useViewScope();

  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setFilterDropdownOpen(false);
      }
    }
    if (filterDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterDropdownOpen]);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const body = { view_scope: viewScope };
        if (agentFilter) body.agent_filter = agentFilter;
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/past-notifications`, body
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        setData(rows.map(r => ({ ...r, event_type: normaliseEventType(r.event_type) })));
      } catch (e) {
        setData([]);
      }
      setLoading(false);
    }
    fetchData();
  }, [viewScope, agentFilter]);

  // When the agent has no notification history at all and isn't narrowing by
  // a filter or search, show a populated sample list instead of an empty
  // state. Demo interactions are sandboxed by useDemoNetworkGuard; the demo
  // clears the moment real data arrives or any filter is applied.
  const hasActiveFilter = !!search || eventFilter !== "all";
  const showDemo = !loading && data.length === 0 && !hasActiveFilter;
  useDemoNetworkGuard(showDemo);
  const displayData = showDemo ? DEMO_PAST_NOTIFICATIONS : data;

  const eventTypes = useMemo(() => {
    const types = new Set(displayData.map(r => r.event_type));
    return Array.from(types).sort((a, b) => (EVENT_LABELS[a] || a).localeCompare(EVENT_LABELS[b] || b));
  }, [displayData]);

  const filtered = useMemo(() => {
    let rows = displayData;
    if (eventFilter !== "all") {
      rows = rows.filter(r => r.event_type === eventFilter);
    }
    if (search) {
      const lower = search.toLowerCase();
      rows = rows.filter(r =>
        (r.email || "").toLowerCase().includes(lower) ||
        (r.address_text || "").toLowerCase().includes(lower)
      );
    }
    return rows;
  }, [displayData, search, eventFilter]);

  return (
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: "#f6f8fa",
      boxSizing: "border-box",
      width: "100%",
      overflow: "auto",
      paddingBottom: showDemo ? (isMobile ? 180 : 110) : 0,
    }}>
      <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#f6f8fa",
          paddingTop: isMobile ? 72 : 32,
          paddingBottom: 8,
          marginLeft: isMobile ? -16 : -32,
          marginRight: isMobile ? -16 : -32,
          paddingLeft: isMobile ? 16 : 32,
          paddingRight: isMobile ? 16 : 32,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", margin: "0 0 8px 0" }}>Notification History</h1>
          </div>
          <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 20, lineHeight: 1.6 }}>
            A log of all visitor activity notifications sent to you — property views, report requests, appraisals, and more.
          </p>

          <div style={{
            display: "flex",
            gap: 12,
            marginBottom: 24,
            flexWrap: "wrap",
          }}>
            {/* Search bar */}
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 14 }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by email or address…"
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: 14,
                  boxSizing: "border-box",
                  background: "#fff",
                  outline: "none",
                }}
              />
            </div>
            {/* Event type filter — custom dropdown */}
            <div ref={filterDropdownRef} style={{ position: "relative", minWidth: 200 }}>
              <button
                onClick={() => setFilterDropdownOpen(prev => !prev)}
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  fontSize: 14,
                  boxSizing: "border-box",
                  background: "#fff",
                  outline: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  position: "relative",
                  color: "#374151",
                  fontWeight: eventFilter !== "all" ? 600 : 400,
                }}
              >
                <FaFilter style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {eventFilter === "all" ? "All event types" : (EVENT_LABELS[eventFilter] || eventFilter)}
                </span>
                <FaChevronDown style={{ fontSize: 10, color: "#9ca3af", flexShrink: 0, marginLeft: 8, transform: filterDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
              </button>
              {filterDropdownOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 300, overflowY: "auto",
                  padding: 4,
                }}>
                  {[{ value: "all", label: "All event types" }, ...eventTypes.map(et => ({ value: et, label: EVENT_LABELS[et] || et }))].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setEventFilter(opt.value); setFilterDropdownOpen(false); }}
                      style={{
                        width: "100%", textAlign: "left", padding: "8px 12px",
                        border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 8,
                        background: eventFilter === opt.value ? "#eff6ff" : "transparent",
                        color: eventFilter === opt.value ? "#2563eb" : "#374151",
                        fontWeight: eventFilter === opt.value ? 600 : 400,
                        transition: "background 0.12s",
                      }}
                      onMouseEnter={e => { if (eventFilter !== opt.value) e.currentTarget.style.background = "#f3f4f6"; }}
                      onMouseLeave={e => { if (eventFilter !== opt.value) e.currentTarget.style.background = "transparent"; }}
                    >
                      {eventFilter === opt.value && <FaCheck style={{ fontSize: 10, color: "#2563eb", flexShrink: 0 }} />}
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{
            background: "#fff", borderRadius: 16, boxShadow: "0 1px 4px #0001",
            border: "1px solid #e5e7eb", overflow: "hidden",
          }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 20px",
                borderBottom: i < 5 ? "1px solid #f3f4f6" : "none",
              }}>
                <Skeleton width={36} height={36} borderRadius={10} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Skeleton width={80} height={18} borderRadius={10} />
                    <Skeleton width={60} height={18} borderRadius={10} />
                  </div>
                  <Skeleton width="55%" height={13} />
                  <Skeleton width="40%" height={12} />
                </div>
                <Skeleton width={50} height={12} />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{
            background: "#fff",
            borderRadius: 12,
            padding: 32,
            boxShadow: "0 2px 8px #0001",
            marginBottom: 24,
            border: "1px solid #e5e7eb",
            textAlign: "center",
          }}>
            <FaBell style={{ fontSize: 40, color: "#d1d5db", marginBottom: 16 }} />
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#1f2937" }}>
              No Results Found
            </h2>
            <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 0 }}>
              Try adjusting your search or filter.
            </p>
          </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0, marginBottom: 40 }}>
            <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 12 }}>
              {filtered.length} notification{filtered.length !== 1 ? "s" : ""}
              {eventFilter !== "all" ? ` · ${EVENT_LABELS[eventFilter] || eventFilter}` : ""}
            </div>

            <div style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
            }}>
              {filtered.map((row, idx) => {
                const eventStyle = EVENT_COLORS[row.event_type] || { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };
                const IconComponent = EVENT_ICONS[row.event_type] || FaBell;
                return (
                  <div
                    key={idx}
                    style={{
                      padding: isMobile ? "14px 16px" : "16px 24px",
                      borderBottom: idx < filtered.length - 1 ? "1px solid #f3f4f6" : "none",
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: isMobile ? "flex-start" : "center",
                      gap: isMobile ? 8 : 16,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#f9fafb"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: eventStyle.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <IconComponent style={{ color: eventStyle.color, fontSize: 15 }} />
                    </div>

                    {/* Main content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{
                          borderRadius: 16,
                          padding: "2px 10px",
                          fontWeight: 600,
                          fontSize: 11,
                          background: eventStyle.bg,
                          color: eventStyle.color,
                          border: `1px solid ${eventStyle.border}`,
                          whiteSpace: "nowrap",
                        }}>
                          {EVENT_LABELS[row.event_type] || row.event_type}
                        </span>
                        <AgentBadge agentName={row.agent_name} />
                        </div>
                      {row.address_text && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <FaHome style={{ color: "#9ca3af", fontSize: 12, flexShrink: 0 }} />
                          <span style={{ fontSize: 14, color: "#374151", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.address_text}
                          </span>
                          <CopyButton text={row.address_text} />
                        </div>
                      )}
                      {row.email && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <FaEnvelope style={{ color: "#9ca3af", fontSize: 11, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: "#6b7280" }}>{row.email}</span>
                          <CopyButton text={row.email} />
                        </div>
                      )}
                    </div>

                    {/* Timestamp + actions */}
                    <div style={{
                      display: "flex",
                      flexDirection: isMobile ? "row" : "column",
                      alignItems: isMobile ? "center" : "flex-end",
                      gap: isMobile ? 8 : 6,
                      flexShrink: 0,
                      flexWrap: "wrap",
                    }}>
                      <div style={{
                        fontSize: 13,
                        color: "#9ca3af",
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                        title={formatFullDate(row.created_at)}
                      >
                        {formatDate(row.created_at)}
                      </div>
                      {row.email && (
                        <a
                          href={`/user-activity?email=${encodeURIComponent(row.email)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#7c3aed",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          <FaHistory style={{ fontSize: 11 }} />
                          View Activity
                        </a>
                      )}
                      {row.email && row.property_id && (row.event_type === "property_analysis" || row.event_type === "property_report_sent") && (
                        <a
                          href={`/leads-property-searches/${btoa(row.email).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}/${row.property_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#0a9fa6",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          <FaExternalLinkAlt style={{ fontSize: 10 }} />
                          View AI Analysis
                        </a>
                      )}
                      {row.address_text && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            const params = new URLSearchParams({ create: "true" });
                            params.set("address", row.address_text);
                            if (row.email) params.set("contact_email", row.email);
                            if (row.property_id) params.set("property_id", row.property_id);
                            navigate(`/leads-property-appraisals?${params.toString()}`);
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#2563eb",
                            background: "#eff6ff",
                            border: "1px solid #bfdbfe",
                            borderRadius: 6,
                            padding: "3px 10px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "#dbeafe"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "#eff6ff"; }}
                        >
                          <FaClipboardCheck style={{ fontSize: 10 }} />
                          Create Appraisal
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {showDemo && (
        <DemoBanner
          isMobile={isMobile}
          title="Here's how Notification History looks with activity"
          subtitle="Sample data — as visitors view properties, request reports, or save notification settings, those alerts will appear here automatically."
        />
      )}
    </div>
  );
}
